import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PersonSearch as PersonSearchIcon } from '@frontierag/frontier-ui-library/icons';
import { ACCOUNT_REPRESENTATIVES_LIMIT } from 'appConstants';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useAccountRepresentatives } from 'contexts/AccountRepresentatives';
import { useDebouncedCallback } from 'use-debounce';

import type { IAccountRepresentatives } from './types';

const getOptionLabel = (option) => `${option.representativeCode} (${option.representativeName})`;

export default function AccountRepresentatives({
  disabled,
  textFieldProps,
  selectedAccount,
  onChange = () => {},
  onClose = () => {},
  productType,
}: IAccountRepresentatives) {
  const { t } = useTranslation('AccountRepresentatives');

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleOnClose = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const handleInputChange = useDebouncedCallback((event, value, reason) => {
    if (reason !== 'reset') setSearchTerm(value);

    if (value.length && reason === 'input') {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, 500);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  const {
    state: { loading, loadedProductType, accountRepresentatives = [], loaded, error },
    actions: { loadAccountRepresentatives },
  } = useAccountRepresentatives();

  useEffect(() => {
    if (!loading && ((!error && !loaded) || loadedProductType !== productType))
      loadAccountRepresentatives({ limit: ACCOUNT_REPRESENTATIVES_LIMIT }, productType);
  }, [loaded, loadAccountRepresentatives, loading, error, productType, loadedProductType]);

  useEffect(() => {
    const selectedRepresentativeName = selectedAccount?.representativeName;

    if (accountRepresentatives.length === 0 || !selectedRepresentativeName) return;

    const found = accountRepresentatives.find(
      (accRep) => accRep.representativeName === selectedRepresentativeName,
    );

    if (!found) {
      onChange('');
    }
  }, [accountRepresentatives, selectedAccount?.representativeName, onChange]);

  return (
    <DynamicAutocomplete
      disabled={disabled}
      textFieldProps={{
        label: t('labels.representativeAccount'),
        placeholder: t('placeholders.representativeAccount'),
        value: searchTerm,
        ...textFieldProps,
      }}
      startAdornmentIcon={<PersonSearchIcon />}
      noOptionsText={t('labels.noOptions')}
      open={open}
      onClose={handleOnClose}
      value={selectedAccount}
      options={accountRepresentatives}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      onChange={handleOnChange}
      loading={loading}
      isOptionEqualToValue={(option, value) =>
        option.representativeCode === value.representativeCode
      }
    />
  );
}
